import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ZonePicker } from "@/components/technicians/ZonePicker";
import { AvailabilityEditor } from "@/components/technicians/AvailabilityEditor";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";

const PROFILE = { type: "INTERNAL", zone_ids: [], specialty_codes: [], can_login: true, assignable: true, company: "", rfc: "", gps_rates: {}, settlement_mode: "mensual", availability: { status: "disponible", days: [], notes: "" } };
const GPS_RATES = [["instalacion", "Instalación"], ["instalacion_duo", "Instalación Duo"], ["desinstalacion", "Desinstalación"], ["revision", "Revisión"], ["mantenimiento", "Mantenimiento"], ["inst_mdvr", "Inst. MDVR"], ["inst_dashcam", "Inst. Dashcam"]];
const GpsRates = ({ p, setP }) => (
  <div className="space-y-2 rounded-lg border border-border p-3" data-testid="user-form-gps-rates">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">Tarifas GPS (pago al técnico)</div>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{GPS_RATES.map(([k, l]) => (
      <label key={k} className="space-y-1 text-xs"><span>{l}</span><Input type="number" min="0" value={p.gps_rates?.[k] ?? ""} onChange={(e) => setP("gps_rates", { ...(p.gps_rates || {}), [k]: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`gps-rate-${k}`} /></label>))}</div>
    <label className="block space-y-1 text-xs"><span>Modalidad de liquidación</span>
      <select className="h-10 w-full rounded-md border border-input bg-transparent px-2 text-sm" value={p.settlement_mode || "mensual"} onChange={(e) => setP("settlement_mode", e.target.value)} data-testid="user-form-settlement-mode"><option value="mensual">Mensual</option><option value="inmediata">Inmediata</option></select></label>
  </div>
);
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;
const Toggle = ({ label, checked, onChange, testid }) => <label className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">{label}<Switch checked={checked} onCheckedChange={onChange} data-testid={testid} /></label>;

const SpecialtyChecks = ({ specialties, value, onChange }) => (
  <div className="grid gap-1.5 sm:grid-cols-2" data-testid="user-form-specialties">
    {specialties.filter((s) => s.kind === "unit" && (s.active || value.includes(s.code))).map((s) => (
      <label key={s.code} className="flex items-center gap-2 text-sm">
        <Checkbox checked={value.includes(s.code)} onCheckedChange={(c) => onChange(c ? [...value, s.code] : value.filter((x) => x !== s.code))} data-testid={`specialty-check-${s.code}`} />{s.name}
      </label>
    ))}
  </div>
);

export const UserDialog = ({ open, onOpenChange, user, zones, specialties, onSaved }) => {
  const [f, setF] = useState({});
  const [showPw, setShowPw] = useState(false);
  useEffect(() => { if (open) setShowPw(false); }, [open]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(user ? { ...user, email: user.email || "", password: "", technician_profile: { ...PROFILE, ...(user.technician_profile || {}) } } : { name: "", email: "", phone: "", role: "technician", active: true, password: "", technician_profile: { ...PROFILE } });
  }, [open, user]);
  if (!f.role) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const p = f.technician_profile;
  const setP = (k, v) => setF((s) => ({ ...s, technician_profile: { ...s.technician_profile, [k]: v } }));
  const tech = f.role === "technician";
  const needsLogin = true;
  const unitCodes = specialties.filter((s) => s.kind === "unit").map((s) => s.code);

  const save = async () => {
    const body = { name: f.name, phone: f.phone || "", role: f.role, active: f.active, email: f.email || null };
    if (f.password) body.password = f.password;
    if (tech) body.technician_profile = { ...p, can_login: true, assignable: true, specialty_codes: p.specialty_codes.filter((c) => unitCodes.includes(c)) };
    setSaving(true);
    try {
      if (user) { await api.patch(`/users/${user.id}`, body); toast.success("Usuario guardado"); }
      else {
        const { data } = await api.post("/users", body);
        toast.success("Usuario guardado");
        if (data.welcome_email?.sent) toast.success(`Correo de bienvenida enviado a ${data.email}`);
        else if (data.welcome_email) toast.error(`Usuario creado, pero no se pudo enviar el correo de bienvenida: ${data.welcome_email.error}`);
      }
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="user-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{user ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Nombre *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="user-form-name" /></F>
            <F label="Rol"><Select value={f.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></F>
            <F label="Teléfono"><Input type="tel" value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="user-form-phone" /></F>
            <F label={needsLogin ? "Correo (usuario) *" : "Correo / contacto (opcional)"}><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="user-form-email" /></F>
          </div>
          {tech && (
            <>
              <F label="Tipo de técnico">
                <RadioGroup value={p.type} onValueChange={(v) => setP("type", v)} className="flex gap-6" data-testid="user-form-tech-type">
                  {[["INTERNAL", "Interno"], ["EXTERNAL", "Eventual / Externo"]].map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 text-sm"><RadioGroupItem value={v} data-testid={`user-form-type-${v.toLowerCase()}`} />{l}</label>
                  ))}
                </RadioGroup>
              </F>
              {p.type === "EXTERNAL" && <div className="grid gap-4 sm:grid-cols-2">
                <F label="Empresa / proveedor (opcional)"><Input value={p.company} onChange={(e) => setP("company", e.target.value)} data-testid="user-form-company" /></F>
                <F label="RFC (opcional)"><Input value={p.rfc} onChange={(e) => setP("rfc", e.target.value.toUpperCase())} data-testid="user-form-rfc" /></F>
              </div>}
              <F label="Especialidades (unidad de servicio)"><SpecialtyChecks specialties={specialties} value={p.specialty_codes} onChange={(v) => setP("specialty_codes", v)} /></F>
              {p.specialty_codes.includes("gps") && <GpsRates p={p} setP={setP} />}
              <F label="Zonas de cobertura"><ZonePicker zones={zones} value={p.zone_ids} onChange={(v) => setP("zone_ids", v)} testid="user-form-zones" /></F>
              <F label="Disponibilidad"><AvailabilityEditor value={p.availability} onChange={(v) => setP("availability", v)} /></F>
            </>
          )}
          {needsLogin && <F label={user?.has_password ? "Nueva contraseña (vacío = sin cambio)" : "Contraseña * (mín. 6)"}><div className="relative"><Input type={showPw ? "text" : "password"} className="pr-10" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="user-form-password" />
            <button type="button" className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"} data-testid="user-form-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></F>}
          <Toggle label="Usuario activo (archivar si se desactiva)" checked={f.active} onChange={(v) => set("active", v)} testid="user-form-active" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !f.name} data-testid="user-form-submit">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
